import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import appSettings from '../../app-settings';
import { getNetworkError, uploadFile } from '../../core/utils';
import { enableMocking } from '../../core/mock-axios';

const ACCOUNTS_URL = `${appSettings.baseUrl}/accounts`;

const EMAIL_TEMPLATES_URL = `${appSettings.baseUrl}/email-templates`;

const createEmailImageUploadUrl = async (templateName) => {
  const response = await axios.post(`${EMAIL_TEMPLATES_URL}/images`, { templateName });
  return response.data;
};

const SET_ERROR = 'account/SET_ERROR';
const SET_SUCCESS = 'account/SET_SUCCESS';
const GET_ACCOUNT = 'account/GET_ACCOUNT';
const UPDATE_ACCOUNT_INFO = 'account/UPDATE_ACCOUNT_INFO';
const UPDATE_EMAIL_PREFERENCES = 'account/UPDATE_EMAIL_PREFERENCES';
const SAVE_EMAIL_PREFERENCES = 'account/SAVE_EMAIL_PREFERENCES';
const SAVE_PROFILE = 'account/SAVE_PROFILE';
const SAVE_PROFILE_FEEDBACK = 'account/SAVE_PROFILE_FEEDBACK';
const SET_ACCOUNT_DOCUMENT = 'account/SET_ACCOUNT_DOCUMENT';
const SET_ONBOARDING_COMPLETE = 'account/SET_ONBOARDING_COMPLETE';
const SET_ONBOARDING_CALL = 'account/SET_ONBOARDING_CALL';
const SET_ACCOUNT_MFA = 'account/SET_ACCOUNT_MFA';
const SET_USER_ROLE = 'account/SET_USER_ROLE';
const SET_USE_STRIPE_NONPROFIT_RATES = 'account/SET_USE_STRIPE_NONPROFIT_RATES';
const SAVE_EMBEDDED_DONATION_PAGES = 'account/SAVE_EMBEDDED_DONATION_PAGES';
const UPDATE_EMBEDDED_DONATION_PAGES = 'account/UPDATE_EMBEDDED_DONATION_PAGES';

export const setError = createAction(SET_ERROR);

export const setSuccess = createAction(SET_SUCCESS);

export const updateAccountInfo = createAction(UPDATE_ACCOUNT_INFO);

export const updateEmailPreferences = createAction(UPDATE_EMAIL_PREFERENCES);

export const setAccountDocument = createAction(SET_ACCOUNT_DOCUMENT);

export const setAccountMfa = createAction(SET_ACCOUNT_MFA);

export const setUserRole = createAction(SET_USER_ROLE);

export const updateEmbeddedDonationPages = createAction(UPDATE_EMBEDDED_DONATION_PAGES);

export const getAccount = createAsyncThunk(GET_ACCOUNT, async () => {
  try {
    const res = await axios.get(`${ACCOUNTS_URL}/current`);
    const account = res.data;

    if (account?.sandbox) {
      enableMocking();
    }

    return account;
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

export const saveEmailPreferences = createAsyncThunk(
  SAVE_EMAIL_PREFERENCES,
  async (logoFile, { getState, dispatch }) => {
    try {
      const {
        domains: { domains },
        account: {
          account: { emailPreferences },
        },
      } = getState();

      let logoUrl = emailPreferences?.logo ?? '';

      if (logoFile) {
        const { uploadUrl, s3Key } = await createEmailImageUploadUrl('logo');
        const arrayBuffer = await logoFile.arrayBuffer();
        await uploadFile(uploadUrl, arrayBuffer, logoFile.type);
        logoUrl = `${appSettings.emailImagesDomain}/${s3Key}`;
      }

      const { logo, replyAddress, ...rest } = emailPreferences;
      const replyAddressName = replyAddress.trim();
      const authenticatedDomain = domains[0]?.valid ? domains[0].domain : null;

      const updateData = {
        ...rest,
        replyAddress: replyAddressName
          ? replyAddressName?.includes('@')
            ? replyAddressName
            : `${replyAddressName}@${authenticatedDomain}`
          : '',
      };

      if (logoUrl) updateData.logo = logoUrl;

      await axios.post(`${ACCOUNTS_URL}/current`, {
        emailPreferences: updateData,
      });

      dispatch(getAccount());
    } catch (err) {
      Sentry.captureException(err);
      const errorMessage = getNetworkError(err);
      throw new Error(errorMessage);
    }
  },
);

export const saveProfile = createAsyncThunk(SAVE_PROFILE, async (profile, { dispatch }) => {
  try {
    await axios.post(`${ACCOUNTS_URL}/current`, {
      profile,
    });

    dispatch(getAccount());
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

// Persists AI-generated Organization Profile feedback to the account so it can
// be displayed on subsequent page loads. Stored as a top-level `profileFeedback`
// attribute on the account-ENV record. Runs after the profile itself is saved,
// so a feedback failure never blocks saving the profile.
export const saveProfileFeedback = createAsyncThunk(SAVE_PROFILE_FEEDBACK, async (profileFeedback, { dispatch }) => {
  try {
    await axios.post(`${ACCOUNTS_URL}/current`, {
      profileFeedback,
    });

    dispatch(getAccount());
    return profileFeedback;
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

export const setOnboardingComplete = createAsyncThunk(SET_ONBOARDING_COMPLETE, async () => {
  try {
    await axios.post(`${ACCOUNTS_URL}/onboarding/complete`);
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

export const setOnboardingCall = createAsyncThunk(SET_ONBOARDING_CALL, async () => {
  try {
    await axios.post(`${ACCOUNTS_URL}/onboarding/call`);
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

export const setUseStripeNonprofitRates = createAsyncThunk(
  SET_USE_STRIPE_NONPROFIT_RATES,
  async (useStripeNonprofitRates) => {
    try {
      await axios.post(`${ACCOUNTS_URL}/current/use-stripe-nonprofit-rates`, {
        useStripeNonprofitRates,
      });

      return useStripeNonprofitRates;
    } catch (err) {
      Sentry.captureException(err);
      const errorMessage = getNetworkError(err);
      throw new Error(errorMessage);
    }
  },
);

export const saveEmbeddedDonationPages = createAsyncThunk(
  SAVE_EMBEDDED_DONATION_PAGES,
  async ({ embeddedDonationPages, defaultEmbeddedDonationPageUrl }, { dispatch }) => {
    try {
      await axios.post(`${ACCOUNTS_URL}/current`, {
        embeddedDonationPages,
        defaultEmbeddedDonationPageUrl,
      });

      dispatch(getAccount());
    } catch (err) {
      Sentry.captureException(err);
      const errorMessage = getNetworkError(err);
      throw new Error(errorMessage);
    }
  },
);
